import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    Box, Paper, Typography, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Snackbar, Alert,
    CircularProgress, FormControl, InputLabel, Select, MenuItem, Chip, TablePagination,
} from "@mui/material";
import { Add as AddIcon, Refresh as RefreshIcon, Category as CategoryIcon, Edit as EditIcon, Close as CloseIcon } from "@mui/icons-material";
import LookupService from "../service/LookupService";
import { extractRows, assertOk } from "../service/Leave/leaveHelpers";

const isActiveValue = (v) =>
    v === true || ["A", "1", "TRUE", "Y"].includes(String(v ?? "").trim().toUpperCase());

const EmployeeCategoryManagement = () => {
const createdBy = useSelector((s) => s.auth?.serviceNo) || "";
const [categories, setCategories] = useState([]);
const [loading, setLoading] = useState(false);
const [saving, setSaving] = useState(false);
const [code, setCode] = useState("");
const [name, setName] = useState("");
const [remarks, setRemarks] = useState("");
const [status, setStatus] = useState("A");
const [editingId, setEditingId] = useState(null);
const [tableSearch, setTableSearch] = useState("");
const [statusFilter, setStatusFilter] = useState("all"); // "all" | "A" | "I"
const [page, setPage] = useState(0);
const rowsPerPage = 5;
const [alert, setAlert] = useState({ open: false, severity: "success", message: "" });

const normalize = (item) => ({
id: String(item.HEC_EMPLOYMENT_CATEGORY_ID ?? ""),
code: String(item.HEC_CATEGORY_CODE ?? ""),
name: String(item.HEC_CATEGORY_NAME ?? ""),
remarks: String(item.HEC_REMARKS ?? ""),
status: isActiveValue(item.HEC_IS_ACTIVE) ? "A" : "I",
});

const loadCategories = useCallback(async () => {
setLoading(true);
try {
    const body = assertOk(await LookupService.getEmployeeCategories());
    setCategories(extractRows(body).map(normalize));
} catch (error) {
    setAlert({ open: true, severity: "error", message: error.message || "Failed to load employee categories." });
} finally {
    setLoading(false);
}
}, []);

useEffect(() => { loadCategories(); }, [loadCategories]);
useEffect(() => { setPage(0); }, [tableSearch, statusFilter]);

const resetForm = () => {
    setEditingId(null);
    setCode(""); setName(""); setRemarks(""); setStatus("A");
};

const handleSelectRow = (cat) => {
    setEditingId(cat.id);
    setCode(cat.code);
    setName(cat.name);
    setRemarks(cat.remarks);
    setStatus(cat.status);
};

const handleSave = async () => {
const trimmedCode = code.trim();
const trimmedName = name.trim();
if (!trimmedCode || !trimmedName) {
    setAlert({ open: true, severity: "error", message: "Both code and name are required." });
    return;
}
const isEditing = editingId !== null;
setSaving(true);
try {
    const payload = { id: editingId, code: trimmedCode, name: trimmedName, createdBy, status, remarks: remarks.trim() };
    const res = isEditing
        ? await LookupService.updateEmployeeCategory(payload)
        : await LookupService.addEmployeeCategory(payload);
    assertOk(res);
    setAlert({ open: true, severity: "success", message: isEditing ? "Employee category updated." : "Employee category added." });
    resetForm();
    await loadCategories();
} catch (error) {
    setAlert({ open: true, severity: "error", message: error.message || (isEditing ? "Failed to update employee category." : "Failed to add employee category.") });
} finally {
    setSaving(false);
}
};

const q = tableSearch.trim().toLowerCase();
const pagedRows = (list) => list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
const filteredCategories = categories
    .filter((c) => statusFilter === "all" || c.status === statusFilter)
    .filter((c) => !q || c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q) || c.remarks.toLowerCase().includes(q));

return (
<Box sx={{ width: "100%", maxWidth: 800, mx: "auto", mt: 4, px: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
    <CategoryIcon sx={{ color: "#142c54", mr: 1 }} />
    <Typography variant="h5" fontWeight={700} color="#142c54">Employee Category</Typography>
    </Box>

    <Paper sx={{ p: 3, mb: 3 }}>
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField size="small" label="Code" value={code} onChange={(e) => setCode(e.target.value)} sx={{ width: 140 }} />
        <TextField size="small" label="Category name" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 1, minWidth: 200 }} />
        <TextField size="small" label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} sx={{ flex: 1, minWidth: 200 }} />
        <FormControl size="small" sx={{ width: 130 }}>
        <InputLabel>Status</InputLabel>
        <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="A">Active</MenuItem>
            <MenuItem value="I">Inactive</MenuItem>
        </Select>
        </FormControl>
        <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : (editingId !== null ? <EditIcon /> : <AddIcon />)}
            onClick={handleSave}
            disabled={saving}
            sx={{ backgroundColor: "#142c54", whiteSpace: "nowrap" }}
        >
        {editingId !== null ? "Update" : "Add"}
        </Button>
        {editingId !== null && (
        <Button variant="outlined" startIcon={<CloseIcon />} onClick={resetForm} disabled={saving} sx={{ whiteSpace: "nowrap" }}>
            Cancel
        </Button>
        )}
    </Box>
    </Paper>

    <Paper>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pt: 2, gap: 2, flexWrap: "wrap" }}>
        <Typography variant="subtitle2" color="text.secondary">
        {filteredCategories.length} of {categories.length} categor{categories.length === 1 ? "y" : "ies"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FormControl size="small" sx={{ width: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="A">Active only</MenuItem>
            <MenuItem value="I">Inactive only</MenuItem>
            </Select>
        </FormControl>
        <TextField
            size="small"
            placeholder="Search by code, name or remarks..."
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            sx={{ width: 240 }}
        />
        <IconButton size="small" onClick={loadCategories} disabled={loading}>
            <RefreshIcon fontSize="small" />
        </IconButton>
        </Box>
    </Box>
    <TableContainer>
        <Table stickyHeader>
        <TableHead>
            <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {loading ? (
            <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>
            ) : filteredCategories.length === 0 ? (
            <TableRow><TableCell colSpan={4} align="center" sx={{ color: "text.secondary", py: 4 }}>No employee categories found.</TableCell></TableRow>
            ) : (
            pagedRows(filteredCategories).map((cat, idx) => (
                <TableRow
                key={`${cat.id}-${idx}`}
                hover
                onClick={() => handleSelectRow(cat)}
                selected={editingId === cat.id}
                sx={{
                    cursor: "pointer",
                    animation: "rowFade 0.25s ease",
                    "@keyframes rowFade": { from: { opacity: 0 }, to: { opacity: 1 } },
                }}
                >
                <TableCell>{cat.code}</TableCell>
                <TableCell>{cat.name}</TableCell>
                <TableCell>{cat.remarks || "—"}</TableCell>
                <TableCell><Chip size="small" label={cat.status === "A" ? "Active" : "Inactive"} color={cat.status === "A" ? "success" : "default"} /></TableCell>
                </TableRow>
            ))
            )}
        </TableBody>
        </Table>
    </TableContainer>
    <TablePagination
        component="div"
        count={filteredCategories.length}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
    />
    </Paper>

    <Snackbar open={alert.open} autoHideDuration={3000} onClose={() => setAlert((a) => ({ ...a, open: false }))}>
    <Alert severity={alert.severity} onClose={() => setAlert((a) => ({ ...a, open: false }))}>{alert.message}</Alert>
    </Snackbar>
</Box>
);
};

export default EmployeeCategoryManagement;